// PRFAQSection.js - Australia
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const PRFAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How do subclasses 189, 190 and 491 differ from each other?',
      answer: 'Subclass 189 is permanent and needs no sponsor of any kind. Subclass 190 is also permanent but depends on a state nominating you, which brings 5 points with it. Subclass 491 is provisional rather than permanent, runs for 5 years, and needs either state nomination or sponsorship by a family member in a regional area, worth 15 points. After 3 years of living and working in regional Australia on a 491, you can apply for permanent residency under subclass 191.'
    },
    {
      question: 'What points score will I actually need?',
      answer: 'Sixty-five points lets you lodge an Expression of Interest, but it does not get you invited. Invitations tend to go to totals of 80-90 and above, depending on your occupation and the round in question. Age, English, work experience and education make up most of the score, and a state nomination adds a further 5 to 15 points on top.'
    },
    {
      question: 'Which English tests does Australia recognise?',
      answer: 'IELTS, PTE Academic, TOEFL iBT, OET and Cambridge English: Advanced (CAE) are all accepted. The floor is Competent English, meaning IELTS 6.0 in each band or the equivalent. Above that, Proficient English (IELTS 7.0) earns 10 points and Superior English (IELTS 8.0) earns 20. Results more than 3 years old will not be accepted.'
    },
    {
      question: 'Is an Australian job offer required?',
      answer: 'Not for General Skilled Migration, so subclasses 189, 190 and 491 can all proceed without one. Some state nomination streams do ask for a job offer, or give preference to applicants who hold one. Employer-sponsored subclass 186 applications are different again and cannot go ahead without an offer from an approved Australian employer. Either way, having an offer strengthens the overall picture and may bring extra points.'
    },
    {
      question: 'What does a skills assessment involve, and how long is it?',
      answer: 'It is a check that your qualifications and work history meet Australian standards for the occupation you have nominated. Each occupation has its own designated assessing authority, such as Engineers Australia, ACS for IT roles, or VETASSESS across a wider range of occupations. Turnaround normally falls between 2 and 4 months, depending on the authority involved and how complicated your history is.'
    },
    {
      question: 'Can my partner and children be included?',
      answer: 'Yes. A spouse or partner and any dependent children can be added to the application, and they receive permanent residency alongside you. A partner with competent English and a positive skills assessment can be worth up to 10 points. Each additional person carries a government charge: AUD $2,320 for anyone aged 18 or over, and AUD $1,160 for a child under 18.'
    },
    {
      question: 'Which state is the easiest to be nominated by?',
      answer: 'South Australia and Tasmania are usually described as the more reachable options, with thresholds around 65-70 points and broader occupation lists. Whether that holds true for you depends on your occupation, your experience and whether you would consider living regionally. We look across the criteria in every state and tell you where your profile stands the best chance.'
    },
    {
      question: 'What changes once permanent residency is granted?',
      answer: 'You have to make your first entry to Australia before the visa expires. From that point you can work without restriction, use Medicare, study, and sponsor eligible relatives. Citizenship becomes available after 4 years of residence, of which the final 12 months must be held as a permanent resident. The status itself is permanent, though travelling overseas after 5 years means you will need a Resident Return Visa (RRV) to come back.'
    },
    {
      question: 'How long does the process run from start to finish?',
      answer: 'It depends on the route. Subclass 189 usually runs 12-24 months in total, state nominated 190 and 491 cases 12-18 months, and employer sponsored 186 cases 12-18 months. Inside those totals sit the skills assessment at 2-4 months, an unpredictable wait for the invitation, and 6-12 months of visa processing. Your occupation, the state of your paperwork and Departmental workload all move the number.'
    },
    {
      question: 'How often do Australian PR applications succeed?',
      answer: 'Complete applications that meet every requirement do well. Where the skills assessment is positive, the points are there and the documentation is in order, approval rates of 85-90% are typical. We do not publish a success rate of our own. Eligibility, points and complete, accurate paperwork are what decide the outcome.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Questions We Hear Often
          </h2>
          <p className="text-gray-600 text-base font-light">
            What applicants ask us most about permanent residency in Australia
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-surface rounded-lg shadow-sm overflow-hidden border border-gray-200"
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base font-semibold text-primary pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-secondary" />
                  ) : (
                    <Plus size={24} className="text-secondary" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-sm font-light leading-relaxed border-t border-gray-200 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Additional Help */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Something we have not covered?</strong> Book a free consultation, tell us about your circumstances, and we will set out the options open to you for Australian permanent residency.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PRFAQSection;
